import React, { useState } from "react";
import {
	Trash2,
	Plus,
	ChevronDown,
	ChevronUp,
	ClipboardList,
	Users,
	MessageCircle,
	Clock,
	Hash,
	Search,
	X,
} from "lucide-react";
import CustomModal from "./CustomModal";

const TrainerWorkouts = ({
	sharedWorkouts,
	allWorkoutScores,
	addSharedWorkout,
	deleteSharedWorkout,
	loading,
}) => {
	const [showForm, setShowForm] = useState(false);
	const [expanded, setExpanded] = useState({});
	const [showActivity, setShowActivity] = useState({});
	const [activitySearch, setActivitySearch] = useState({});
	const [publishing, setPublishing] = useState(false);
	const [newWorkout, setNewWorkout] = useState({
		title: "",
		foundation: "",
		build: "",
		peak: "",
	});
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "delete",
		onConfirm: null,
	});

	const handleCreate = async (e) => {
		e.preventDefault();
		if (!newWorkout.title.trim()) {
			setModal({
				show: true,
				title: "Missing Title",
				message: "Please enter a workout name.",
				type: "warning",
			});
			return;
		}
		if (
			!newWorkout.foundation.trim() &&
			!newWorkout.build.trim() &&
			!newWorkout.peak.trim()
		) {
			setModal({
				show: true,
				title: "Missing Content",
				message: "Please enter at least one category.",
				type: "warning",
			});
			return;
		}
		setPublishing(true);
		try {
			await addSharedWorkout({
				title: newWorkout.title.trim(),
				foundation: newWorkout.foundation.trim(),
				build: newWorkout.build.trim(),
				peak: newWorkout.peak.trim(),
			});
			setNewWorkout({ title: "", foundation: "", build: "", peak: "" });
			setShowForm(false);
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
		setPublishing(false);
	};

	const handleDelete = (workoutId, title) => {
		setConfirmModal({
			show: true,
			title: "Delete Workout",
			message: `Are you sure you want to delete "${title}"?`,
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await deleteSharedWorkout(workoutId);
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message,
						type: "error",
					});
				}
			},
		});
	};

	const getScoresForWorkout = (workoutId) => {
		if (!allWorkoutScores) return [];
		return allWorkoutScores
			.filter((s) => s.workoutId === workoutId)
			.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
	};

	return (
		<div className="card">
			<div className="card-header d-flex justify-content-between align-items-center">
				<h5 className="mb-0">
					<ClipboardList className="me-2" size={20} />
					Workout Management
				</h5>
				<button
					className="btn btn-primary btn-sm"
					onClick={() => setShowForm(!showForm)}
					disabled={loading}
				>
					{showForm ? (
						"Cancel"
					) : (
						<>
							<Plus size={16} className="me-1" />
							Publish Workout
						</>
					)}
				</button>
			</div>

			<div className="card-body">
				{showForm && (
					<div className="mb-4 p-3 bg-light rounded workout-form-mobile">
						<h6>New Workout</h6>
						<form onSubmit={handleCreate}>
							<div className="row g-3">
								<div className="col-12">
									<label className="form-label">Workout Name *</label>
									<input
										type="text"
										className="form-control"
										value={newWorkout.title}
										onChange={(e) =>
											setNewWorkout({ ...newWorkout, title: e.target.value })
										}
										placeholder="e.g. Monday Burner"
										disabled={publishing}
									/>
								</div>
								<div className="col-12">
									<label className="form-label">Foundation</label>
									<textarea
										className="form-control"
										rows="9"
										value={newWorkout.foundation}
										onChange={(e) =>
											setNewWorkout({
												...newWorkout,
												foundation: e.target.value,
											})
										}
										placeholder="Enter Foundation workout details..."
										disabled={publishing}
									/>
								</div>
								<div className="col-12">
									<label className="form-label">Build</label>
									<textarea
										className="form-control"
										rows="9"
										value={newWorkout.build}
										onChange={(e) =>
											setNewWorkout({ ...newWorkout, build: e.target.value })
										}
										placeholder="Enter Build workout details..."
										disabled={publishing}
									/>
								</div>
								<div className="col-12">
									<label className="form-label">Peak</label>
									<textarea
										className="form-control"
										rows="9"
										value={newWorkout.peak}
										onChange={(e) =>
											setNewWorkout({ ...newWorkout, peak: e.target.value })
										}
										placeholder="Enter Peak workout details..."
										disabled={publishing}
									/>
								</div>
								<div className="col-12">
									<div className="d-flex gap-2">
										<button
											type="submit"
											className="btn btn-success flex-grow-1 flex-md-grow-0"
											disabled={publishing}
										>
											{publishing ? "Publishing..." : "Publish Workout"}
										</button>
										<button
											type="button"
											className="btn btn-secondary flex-grow-1 flex-md-grow-0"
											onClick={() => setShowForm(false)}
											disabled={publishing}
										>
											Cancel
										</button>
									</div>
								</div>
							</div>
						</form>
					</div>
				)}

				<h6 className="mb-3">Published Workouts ({sharedWorkouts.length})</h6>
				{sharedWorkouts.length === 0 ? (
					<div className="text-center py-5">
						<p className="text-muted">No workouts published yet.</p>
					</div>
				) : (
					<div className="list-group">
						{sharedWorkouts.map((workout) => {
							const isExpanded = expanded[workout.id];
							return (
								<div
									key={workout.id}
									className="list-group-item mb-2"
									style={{ borderRadius: "8px" }}
								>
									<div
										className="d-flex justify-content-between align-items-center"
										onClick={() =>
											setExpanded((prev) => ({
												...prev,
												[workout.id]: !prev[workout.id],
											}))
										}
										style={{ cursor: "pointer" }}
									>
										<div>
											<h6 className="mb-0">{workout.title}</h6>
											<small className="text-muted">
												{new Date(workout.createdAt).toLocaleDateString()}
											</small>
										</div>
										<div className="d-flex align-items-center gap-2">
											<button
												className="btn btn-sm btn-outline-danger"
												onClick={(e) => {
													e.stopPropagation();
													handleDelete(workout.id, workout.title);
												}}
												disabled={loading}
											>
												<Trash2 size={14} />
											</button>
											{isExpanded ? (
												<ChevronUp size={20} />
											) : (
												<ChevronDown size={20} />
											)}
										</div>
									</div>
									{isExpanded && (
										<div className="mt-3">
											{workout.foundation && (
												<div className="mb-2">
													<span className="badge bg-success me-2">
														Foundation
													</span>
													<p
														className="mb-1 ms-2"
														style={{ whiteSpace: "pre-wrap" }}
													>
														{workout.foundation}
													</p>
												</div>
											)}
											{workout.build && (
												<div className="mb-2">
													<span className="badge bg-warning text-dark me-2">
														Build
													</span>
													<p
														className="mb-1 ms-2"
														style={{ whiteSpace: "pre-wrap" }}
													>
														{workout.build}
													</p>
												</div>
											)}
											{workout.peak && (
												<div className="mb-2">
													<span className="badge bg-danger me-2">Peak</span>
													<p
														className="mb-1 ms-2"
														style={{ whiteSpace: "pre-wrap" }}
													>
														{workout.peak}
													</p>
												</div>
											)}

											{/* User Activity Section */}
											{(() => {
												const scores = getScoresForWorkout(workout.id);
												const isActivityOpen = showActivity[workout.id];
												const searchTerm = (
													activitySearch[workout.id] || ""
												).trim();
												const filteredScores = searchTerm
													? scores.filter((s) =>
															(s.userName || "")
																.toLowerCase()
																.includes(searchTerm.toLowerCase()),
														)
													: scores;
												return (
													<div
														className="mt-3 pt-3"
														style={{ borderTop: "1px solid #dee2e6" }}
													>
														<div className="d-flex flex-wrap align-items-center gap-2 mb-2">
															<button
																className="btn btn-sm btn-outline-info"
																onClick={(e) => {
																	e.stopPropagation();
																	setShowActivity((prev) => ({
																		...prev,
																		[workout.id]: !prev[workout.id],
																	}));
																}}
															>
																<Users size={14} className="me-1" />
																{isActivityOpen ? "Hide" : "View"} User Activity
																{scores.length > 0 && ` (${scores.length})`}
																{isActivityOpen ? (
																	<ChevronUp size={14} className="ms-1" />
																) : (
																	<ChevronDown size={14} className="ms-1" />
																)}
															</button>

															{isActivityOpen && scores.length > 0 && (
																<div
																	className="input-group input-group-sm"
																	style={{ maxWidth: "260px" }}
																	onClick={(e) => e.stopPropagation()}
																>
																	<span className="input-group-text">
																		<Search size={14} />
																	</span>
																	<input
																		type="text"
																		className="form-control"
																		placeholder="Search by name..."
																		value={activitySearch[workout.id] || ""}
																		onChange={(e) =>
																			setActivitySearch((prev) => ({
																				...prev,
																				[workout.id]: e.target.value,
																			}))
																		}
																	/>
																	{searchTerm && (
																		<button
																			className="btn btn-outline-secondary"
																			type="button"
																			onClick={() =>
																				setActivitySearch((prev) => ({
																					...prev,
																					[workout.id]: "",
																				}))
																			}
																			aria-label="Clear search"
																		>
																			<X size={14} />
																		</button>
																	)}
																</div>
															)}
														</div>

														{isActivityOpen && (
															<div>
																{scores.length === 0 ? (
																	<p className="text-muted small mt-2">
																		No scores logged for this workout yet.
																	</p>
																) : filteredScores.length === 0 ? (
																	<p className="text-muted small mt-2">
																		No results for "{searchTerm}".
																	</p>
																) : (
																	<>
																		{/* Desktop table */}
																		<div className="table-responsive d-none d-md-block">
																			<table className="table table-sm table-hover mb-0">
																				<thead>
																					<tr>
																						<th>User</th>
																						<th>Date</th>
																						<th>Category</th>
																						<th>Type</th>
																						<th>Score</th>
																						<th>Comment</th>
																					</tr>
																				</thead>
																				<tbody>
																					{filteredScores.map((s) => (
																						<tr key={s.id}>
																							<td>
																								<strong>
																									{s.userName || "Unknown"}
																								</strong>
																							</td>
																							<td>
																								{new Date(
																									s.completedAt,
																								).toLocaleDateString()}
																							</td>
																							<td>
																								<span
																									className={`badge ${s.category === "foundation" ? "bg-success" : s.category === "build" ? "bg-warning text-dark" : "bg-danger"}`}
																								>
																									{s.category}
																								</span>
																							</td>
																							<td>
																								{s.scoreType === "time" ? (
																									<Clock
																										size={14}
																										className="me-1"
																									/>
																								) : (
																									<Hash
																										size={14}
																										className="me-1"
																									/>
																								)}
																								{s.scoreType === "time"
																									? "Time"
																									: "Reps"}
																							</td>
																							<td>{s.score}</td>
																							<td>
																								{s.comment ? (
																									<span
																										style={{
																											fontStyle: "italic",
																										}}
																									>
																										<MessageCircle
																											size={12}
																											className="me-1"
																										/>
																										{s.comment}
																									</span>
																								) : (
																									<small className="text-muted">
																										—
																									</small>
																								)}
																							</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>

																		{/* Mobile card layout */}
																		<div className="d-md-none">
																			{filteredScores.map((s) => (
																				<div
																					key={s.id}
																					className="mb-2 p-2"
																					style={{
																						backgroundColor: "#1a1a1a",
																						borderRadius: "8px",
																						border: "1px solid #333",
																					}}
																				>
																					<div className="d-flex justify-content-between align-items-start mb-1">
																						<strong style={{ color: "#fff" }}>
																							{s.userName || "Unknown"}
																						</strong>
																						<span
																							className={`badge ${s.category === "foundation" ? "bg-success" : s.category === "build" ? "bg-warning text-dark" : "bg-danger"}`}
																							style={{ fontSize: "0.7rem" }}
																						>
																							{s.category}
																						</span>
																					</div>
																					<div
																						className="d-flex flex-wrap gap-2 align-items-center"
																						style={{ fontSize: "0.85rem" }}
																					>
																						<span
																							style={{
																								color: "rgba(255,255,255,0.6)",
																							}}
																						>
																							{new Date(
																								s.completedAt,
																							).toLocaleDateString()}
																						</span>
																						<span
																							style={{
																								color: "rgba(255,255,255,0.6)",
																							}}
																						>
																							{s.scoreType === "time" ? (
																								<Clock
																									size={12}
																									className="me-1"
																								/>
																							) : (
																								<Hash
																									size={12}
																									className="me-1"
																								/>
																							)}
																							{s.score}
																						</span>
																					</div>
																					{s.comment && (
																						<div
																							className="mt-1"
																							style={{
																								fontSize: "0.8rem",
																								color: "rgba(255,255,255,0.45)",
																								fontStyle: "italic",
																							}}
																						>
																							<MessageCircle
																								size={11}
																								className="me-1"
																							/>
																							{s.comment}
																						</div>
																					)}
																				</div>
																			))}
																		</div>
																	</>
																)}
															</div>
														)}
													</div>
												);
											})()}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type={confirmModal.type}
				onConfirm={confirmModal.onConfirm}
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				confirmText="Delete"
				loading={loading}
			/>
		</div>
	);
};

export default TrainerWorkouts;
